import type {CommitsPageData} from '../page-data/payloads/commits'
import type {HeaderPageData} from '../page-data/payloads/header'
import type {FilesRoutePayload as OriginalFilesRoutePayload} from '../page-data/payloads/files'
import type {TimelineItem} from '../page-data/payloads/timeline'
import type {DescriptionEditInformation} from '../page-data/loaders/use-description-data'
import type {MergeabilityLiveUpdateChannels} from '@github-ui/mergebox/use-mergeability-live-updates'
import type {MergeMethod} from '@github-ui/mergebox/types'
import type {SafeHTMLString} from '@github-ui/safe-html'

export type LayoutRoutePayload = HeaderPageData

export type CommitsRouteWithHeaderDataPayload = CommitsPageData &
  HeaderPageData & {
    metadata: {
      deferredCommitsDataUrl: string
      commitHeadShaChannel: string
    }
  }

export type CommitsRoutePayload = CommitsPageData & {
  metadata: {
    deferredCommitsDataUrl: string
    commitHeadShaChannel: string
  }
}

export type FilesRoutePayload = OriginalFilesRoutePayload

export type ChangesRoutePayload = OriginalFilesRoutePayload

export type BaseComparisonInfoRoutePayload = OriginalFilesRoutePayload

export type ConversationRoutePayload = {
  id: string
  aliveChannel: string
  markAsReadChannel: string | undefined
  locked: boolean
  helpUrl: string
  mergeboxChannels: MergeabilityLiveUpdateChannels
  defaultMergeMethod: MergeMethod
}

export type OverviewRoutePayload = {
  description: SafeHTMLString | null
  descriptionText: string | null
  editInformation?: DescriptionEditInformation
  sinceSelectors: SinceSelector[]
  lastActivityTimestamp: string | null
  user?: OverviewUser
  repoHasDeployments?: boolean
}

export interface OverviewUser {
  canClose: boolean
  canReopen: boolean
  cannotReopenReason?: string | null
  canConvertToDraft: boolean
  canMarkReadyForReview: boolean
  canAssign: boolean
  canLabel: boolean
  canRevert: boolean
}

export const SinceSource = {
  Created: 'created',
  LastReview: 'last_review',
  LastInteracted: 'last_interacted',
  ReadyForReview: 'ready_for_review',
} as const

export type SinceSource = (typeof SinceSource)[keyof typeof SinceSource]

export type ActivityPayload = {
  summary: TimelineSummary
  timeline: TimelineItem[]
}

export type SinceSelector = {
  timestamp: string
  source: SinceSource
  selected: boolean
}

export type TimelineSummary = {
  approvals: ReviewTimelineSummaryItem
  changesRequested: ReviewTimelineSummaryItem
  comments: ReviewTimelineSummaryItem
  issueComments: IssueCommentTimelineSummaryItem
  commits: CommitTimelineSummaryItem
  closed: BaseTimelineSummaryItem
  reopened: BaseTimelineSummaryItem
  markedAsDraft: BaseTimelineSummaryItem
  readyForReview: BaseTimelineSummaryItem
  labeled: LabeledTimelineSummaryItem
  reviewRequested: ReviewRequestedTimelineSummaryItem
  forcePushed: ForcePushedTimelineSummaryItem
  merged: MergedTimelineSummaryItem
  baseChanged: BaseChangedTimelineSummaryItem
  descriptionUpdated: BaseTimelineSummaryItem
  total: number
}

export type BaseTimelineSummaryItem = {
  count: number
  events: BaseTimelineEvent[]
}

export type ReviewTimelineSummaryItem = {
  count: number
  events: ReviewTimelineEvent[]
}

export type CommitTimelineSummaryItem = {
  count: number
  events: CommitTimelineEvent[]
}

export type LabeledTimelineSummaryItem = {
  count: number
  events: LabeledTimelineEvent[]
}

export type ReviewRequestedTimelineSummaryItem = {
  count: number
  events: ReviewRequestedTimelineEvent[]
}

export type ForcePushedTimelineSummaryItem = {
  count: number
  events: ForcePushedTimelineEvent[]
}

export type MergedTimelineSummaryItem = {
  count: number
  events: MergedTimelineEvent[]
}

export type BaseChangedTimelineSummaryItem = {
  count: number
  events: BaseChangedTimelineEvent[]
}

export type IssueCommentTimelineSummaryItem = {
  count: number
  events: IssueCommentTimelineEvent[]
}

export type BaseTimelineEvent = {
  timestamp: string
  user: TimelineUser
}

export type IssueCommentTimelineEvent = BaseTimelineEvent & {
  additionalInfo: {
    commentId: number
  }
}

export type ReviewTimelineEvent = BaseTimelineEvent & {
  additionalInfo: {
    reviewId: number
    commentCount: number
    requestedReviewerTeams?: string[]
    reviewedCommitOid?: string
  }
}

export type CommitTimelineEvent = BaseTimelineEvent & {
  additionalInfo: {
    sha: string
    baseSha?: string
    shortMessageText?: string
  }
}

export type LabeledTimelineEvent = BaseTimelineEvent & {
  additionalInfo: {
    eventType: 'labeled' | 'unlabeled'
    labelName: string
  }
}

export type ReviewRequestedTimelineEvent = BaseTimelineEvent & {
  additionalInfo: {
    eventType: 'review_requested' | 'review_request_removed'
    requestedReviewer: string
  }
}

export type ForcePushedTimelineEvent = BaseTimelineEvent & {
  additionalInfo: {
    beforeSha?: string
    afterSha?: string
  }
}

export type MergedTimelineEvent = BaseTimelineEvent & {
  additionalInfo?: {
    mergeMethod?: string
  }
}

export type BaseChangedTimelineEvent = BaseTimelineEvent & {
  additionalInfo: {
    oldBaseRef?: string
    newBaseRef?: string
  }
}

export type TimelineEvent =
  | BaseTimelineEvent
  | IssueCommentTimelineEvent
  | ReviewTimelineEvent
  | CommitTimelineEvent
  | LabeledTimelineEvent
  | ReviewRequestedTimelineEvent
  | ForcePushedTimelineEvent
  | MergedTimelineEvent
  | BaseChangedTimelineEvent

export type TimelineUser = {
  id: number
  login: string
  avatarUrl: string
}
